import { useConfig } from "@/core/config/ConfigContext";
import { UseMutationOptions, UseMutationResult } from "@tanstack/react-query";
import { ReadStoreGroup, WriteStoreGroup } from "@/types/storeGroup";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { Module } from "@/constants";
import { useServices } from "@/core/services/useServices";
import { LegacyStoreGroupService } from "../service";

interface CreateMutationOptions
  extends Omit<
    UseMutationOptions<ReadStoreGroup, Error, WriteStoreGroup, unknown>,
    "mutationFn"
  > {
  errorTitle?: string;
}

export function useCreateStoreGroup(
  options?: CreateMutationOptions,
): UseMutationResult<ReadStoreGroup, Error, WriteStoreGroup, unknown> {
  const config = useConfig();
  const useNewService = config.isFeatureEnabled("newStoreGroupServiceEnabled");
  const { storeGroups } = useServices();
  const { invalidateListQuery } = useQueryCacheUpdater(Module.StoreGroups);

  return useMutationWithErrorHandling<
    ReadStoreGroup,
    Error,
    WriteStoreGroup,
    unknown
  >({
    ...options,
    mutationFn: (data: WriteStoreGroup) => {
      if (useNewService) {
        return storeGroups.createStoreGroup(data);
      }

      return LegacyStoreGroupService.post<WriteStoreGroup, ReadStoreGroup>(
        data,
      );
    },
    onSuccess: async (data, variables, context) => {
      await invalidateListQuery();

      options?.onSuccess?.(data, variables, context);
    },
  });
}
